/**
 * The spatial canvas — the same graph, seen from inside.
 *
 * ── The grammar, and what changed from the flat view ──────────────────────
 *
 * Under perspective, apparent size is distance. That is not a bug to be
 * compensated away; it is the only reason a projected scene is legible at all. So
 * this view hands size and thickness to the camera, and moves the two magnitudes
 * the flat canvas kept there onto channels the camera cannot touch:
 *
 *   hue                →  what kind of move it is           (as in 2D)
 *   glyph              →  open / contradiction / insight / disputed / checked /
 *                         a check citing a retracted work /
 *                         an objection below it stood       (as in 2D)
 *   HEIGHT             →  how much outside contact the claim has had  (new)
 *   strand opacity     →  confidence                        (was thickness)
 *   node glow strength →  novelty                           (was radius)
 *   screen size        →  distance from the eye             (means nothing else)
 *
 * Depth is carried by HUE rather than by alpha — far marks wash toward the
 * background colour instead of fading out — precisely so that opacity is free to
 * mean confidence. Getting this backwards is the whole trap: haze on alpha plus
 * confidence on alpha, and a distant certainty is indistinguishable from a nearby
 * guess.
 *
 * ── Why no 3D library ─────────────────────────────────────────────────────
 *
 * There is no lighting model to import. The flat canvas is emissive marks on a
 * night field or ink on a warm plate, and every mark in it is a 2D path with a
 * semantic colour. Projecting the geometry by hand and drawing it with the SAME
 * path vocabulary keeps the whole visual language — and, more to the point, keeps
 * exact control over which properties perspective is allowed to touch. A mesh
 * renderer would scale everything, including the things that must not scale.
 *
 * Ribbons are built in SCREEN space from a projected polyline, with each vertex's
 * width taken from its own perspective scale. That gives a strand that thins
 * correctly as it recedes, from the same `ribbon()` the flat view uses.
 *
 * ── The stems ─────────────────────────────────────────────────────────────
 *
 * A mark floating above a plane has no readable altitude — the eye cannot tell a
 * high near thing from a low far one. Every raised node drops a thin line to the
 * ground, which is what converts the evidence axis from a claim into something
 * you can actually read. It is the one mark here with no counterpart in 2D, and
 * it is not decoration: it is the axis.
 */

import type { Filters, ReasonNode, Session } from '../core/types'
import {
  channelOf,
  checkedAgainstSources,
  hollowGlyph,
  kindChannelOf,
  liveChannelOf,
} from '../core/types'
import { drawnGlowOf, drawnRatingsOf, writtenAt } from '../core/rewrite'
import { ringRadius } from '../core/layout'
import { motion } from '../core/motion'
import { pulseLevel, pulses } from '../core/pulse'
import { BASE } from '../core/tempo'
import { growth } from './growth'
import { addPoly } from './path'
import { groundAfterResize } from './ground'
import { citesRetracted, traceBrokenRim } from './rim'
import { traceNotched } from './notch'
import { stoodAgainst } from '../core/endings'
import { screen } from './screen'
import { taintMap } from '../core/taint'
import { PRIOR_VERSION_INK, evidenceStaleMap, priorVersionIds } from '../core/rebase'
import type { Palette, RGB } from '../core/palette'
import type { Field } from '../core/framing'
import { hot, hoverRingAlpha, mix, rgba, washed } from '../core/palette'
import {
  bridgeArc,
  contactHeight,
  contactTier,
  liftPath,
  TIER_HEIGHT,
  makeProjector,
  foreignStation,
  type Cam,
  type Projected,
  type Projector,
  type Vec3,
} from '../core/space'
import {
  type Pt,
  TAU,
  clamp,
  easeOutBack,
  easeOutCubic,
  hash01,
  ribbon,
  strandPath,
  tracePoly,
} from '../core/geometry'

/** The frame the flat renderer already derives. Reused rather than re-derived:
 *  visibility, folds, focus and the palette mean the same thing in both views,
 *  and two derivations of one set is how two views come to disagree about what
 *  is on screen. `view` is the one field this renderer ignores. */
export interface SpatialFrame {
  session: Session
  visible: Set<string>
  folded: Map<string, number>
  focus: Set<string> | null
  /** Transient coach emphasis. It never replaces the user's focus. */
  guideKeep: Set<string> | null
  guideTargetId: string | null
  selectedId: string | null
  hoverId: string | null
  activeTargets: string[]
  filters: Filters
  palette: Palette
  rev: number
  tempo: number
  playhead: number | null
  playheadAt: number
  /** The footer's Rings switch (D369), which takes the ground's depth rings too. */
  rings: boolean
}

/**
 * World sizes, derived rather than guessed.
 *
 * At a framing distance the scale is pinned by the geometry: `fitDistance` puts the
 * graph's radius `FIT_EDGE` inside half the shorter axis of the visible field, so
 * pixels-per-world-unit comes out at about `(shorter / 2 − FIT_EDGE) / radius` —
 * around 0.22 for an ordinary field and a depth-six graph, NOT the ~0.5 a flat `k`
 * usually sits at. The first pass here
 * assumed the flat figure and every mark landed at half the size it should be.
 *
 * So these are back-solved from what the flat canvas draws: a node reads at about
 * six pixels and a strand at two or three when the whole graph is in frame, and
 * both grow as you move in — which is the part perspective is for.
 *
 * The orb keeps the flat view's 108, which was already a world size over there.
 */
const NODE_R = 26
const STRAND_W = 11
const ROOT_R = 108

/** Screen clamps. A mark smaller than this is a dirty pixel; larger than this and
 *  one node fills the stage and the view stops being a view. */
const R_MIN = 1.1
const R_MAX = 74

/** How far the bottom band must move before the picture re-centres on it — a
 *  couple of the composer's lines. See `aim`. */
const BAND_SLACK = 48

/** One drawable, already projected. Painter-sorted, so this is flat and cheap. */
type Item =
  | {
      kind: 'strand'
      z: number
      n: ReasonNode
      pts: Pt[]
      scales: number[]
      stale: boolean
      /** The geometry key the path was lifted under — see `paths`. */
      rev: number
    }
  | {
      kind: 'bridge'
      z: number
      n: ReasonNode
      pts: Pt[]
      scales: number[]
      foreign: boolean
      stale: boolean
    }
  | { kind: 'stem'; z: number; n: ReasonNode; a: Projected; b: Projected }
  | { kind: 'node'; z: number; n: ReasonNode; at: Projected }
  | { kind: 'root'; z: number; n: ReasonNode; at: Projected }

export class Spatial {
  private ctx: CanvasRenderingContext2D
  private dpr = 1
  private w = 0
  private h = 0
  private calm = false
  private tempo = 1
  private birthAt = new Map<string, number>()
  /** Last frame's projections, for hit testing and for the label layer. */
  private placed = new Map<string, Projected>()
  private origin: Projected | null = null
  private items: Item[] = []
  /**
   * Each strand's lifted world-space path, and each local bridge's arc, per
   * revision or motion epoch — the cache the flat view keeps for its centre
   * lines, which this view had none of: every frame re-sampled every curve,
   * lifted it and projected it (docs/performance-audit.md, finding 8). The
   * projection still runs per frame, because the camera is what moves here.
   */
  private paths = new Map<string, { rev: number; path: Vec3[] }>()
  private arcs = new Map<string, { rev: number; path: Vec3[] }>()
  /**
   * Settled strands' projected ribbons, valid while the camera is the very
   * object they were projected through: the store replaces `cam` on every
   * eased frame and leaves it alone at rest, so identity is the right key.
   */
  private ribbons = new Map<string, { cam: Cam; rev: number; stale: boolean; path: Path2D }>()
  private cam: Cam | null = null
  /** Last frame's camera, revision, timeline and size — what `screen.frame` is
   *  bumped against. See `screen.ts`. */
  private lastCam: Cam | null = null
  private lastRev = -1
  private lastPlayhead: number | null = null
  private lastPlayheadAt = NaN
  private lastW = 0
  private lastH = 0

  constructor(private canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) throw new Error('Canvas 2D unavailable')
    this.ctx = ctx
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.calm = mq.matches
    mq.addEventListener('change', (e) => {
      this.calm = e.matches
    })
  }

  resize(w: number, h: number) {
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    if (this.w === w && this.h === h && this.dpr === dpr) return
    this.w = w
    this.h = h
    this.dpr = dpr
    this.canvas.width = Math.max(1, Math.round(w * dpr))
    this.canvas.height = Math.max(1, Math.round(h * dpr))
    groundAfterResize(this.canvas, this.ctx)
    // The projector is a function of the stage size, so every projected ribbon
    // is stale the moment it changes.
    this.ribbons.clear()
  }

  get size() {
    return { w: this.w, h: this.h }
  }

  /* ── the picture's centre ────────────────────────────────────────────── */

  /**
   * Where the projection is centred: the middle of the part of the stage the
   * reader can see, not the middle of the stage.
   *
   * The flat camera has framed into the visible field since D159; this view kept
   * the stage's centre, and the stage runs under the panes. At 1440 the sidebar
   * and the Thread cover 760px of it, so the seed — the point every fit and every
   * double-click puts at the centre, and the pivot every orbit turns about — sat
   * under the Thread's edge, and the graph's near half with it. On a phone it sat
   * under the composer. An orbit about a point you cannot see is the most
   * disorienting thing an orbit rig does (the header of `SpatialCanvas`).
   *
   * So the principal point moves, and nothing else: the camera is the reader's,
   * the focal length stays the stage's (a pane opening does not zoom the scene),
   * and `project` is the stage's projection plus one offset — which is why a
   * node the store judges against the whole stage can only be judged MORE
   * generously than here, never less (`seen`).
   *
   * The columns are honoured as they move; they move when somebody opens, closes
   * or drags a pane. The bottom band is honoured past `BAND_SLACK` only: the
   * composer grows a line when a node is picked and a status row when a pass is
   * running, and a volume that slid half a line up under the reader every time
   * one did would be a landing moving the view by the back door (invariant 11).
   * The phone's sheet opening is hundreds of pixels, and is honoured.
   */
  private centre = { x: 0, y: 0 }
  private centreAim: { x: number; y: number } | null = null
  /** The centre is still easing toward its aim. */
  private centring = false

  aim(field: Field) {
    const next = { x: (field.x0 + field.x1) / 2, y: (field.y0 + field.y1) / 2 }
    const was = this.centreAim
    if (!was) {
      // The first aim is where the picture starts: nothing to ease from.
      this.centreAim = next
      this.centre = { ...next }
      return
    }
    this.centreAim = {
      x: next.x,
      y: Math.abs(next.y - was.y) > BAND_SLACK ? next.y : was.y,
    }
  }

  /** Ease the centre toward its aim; answers whether it moved this frame. The
   *  camera's own smoothing constant (`SpatialCanvas`), and the snap under
   *  reduced motion that every eased thing here takes. */
  private easeCentre(dt: number): boolean {
    const to = this.centreAim
    if (!to) return false
    const dx = to.x - this.centre.x
    const dy = to.y - this.centre.y
    if (dx === 0 && dy === 0) {
      this.centring = false
      return false
    }
    const a = this.calm ? 1 : 1 - Math.exp(-dt / 300)
    const done = Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5
    this.centre = done ? { ...to } : { x: this.centre.x + dx * a, y: this.centre.y + dy * a }
    this.centring = !done && a < 1
    return true
  }

  /** The centre this frame projects about: the stage's middle until the host
   *  has aimed it at a field. */
  private get middle(): { x: number; y: number } {
    return this.centreAim ? this.centre : { x: this.w / 2, y: this.h / 2 }
  }

  /** The stage's projection, centred on `c` instead of the stage's middle. */
  private projector(cam: Cam, c: { x: number; y: number }): Projector {
    const base = makeProjector(cam, this.w, this.h)
    const ox = c.x - this.w / 2
    const oy = c.y - this.h / 2
    if (ox === 0 && oy === 0) return base
    return {
      ...base,
      project: (x, y, z) => {
        const q = base.project(x, y, z)
        // A fresh object per call, so it is ours to move.
        if (q.on) {
          q.x += ox
          q.y += oy
        }
        return q
      },
    }
  }

  /**
   * Whether a node stands inside the visible field, clear of its edges by
   * `margin`, from `cam` — the camera the reader is heading for. The flat view's
   * `lookView` question, for the orbit rig: a node picked from a list is flown
   * to only if this says no (`SpatialCanvas`).
   */
  seen(cam: Cam, n: ReasonNode, field: Field, margin: number): boolean {
    const c = this.centreAim ?? this.middle
    const q = this.projector(cam, c).project(n.x, contactHeight(n), n.y)
    return (
      q.on &&
      q.x >= field.x0 + margin &&
      q.x <= field.x1 - margin &&
      q.y >= field.y0 + margin &&
      q.y <= field.y1 - margin
    )
  }

  private born(n: ReasonNode): number {
    return this.birthAt.get(n.id) ?? n.birth
  }

  /** Where a node sits in the world: its flat position on the ground plane, at the
   *  height its evidence earns. Reading the EASED position rather than the layout
   *  target is what makes a fold retract in 3D too, for free. */
  private worldOf(n: ReasonNode): Vec3 {
    const at = motion.at(n)
    return { x: at.x, y: contactHeight(n), z: at.y }
  }

  /* ── depth as colour ─────────────────────────────────────────────────── */

  /**
   * How much a mark at this depth washes toward the background.
   *
   * Keyed to the camera's own distance rather than to absolute world units, so
   * pulling back to take in the whole graph does not grey the entire thing out —
   * haze should say "further than the things around it", not "far from the
   * origin".
   */
  private haze(z: number, dist: number): number {
    return clamp((z - dist * 0.42) / (dist * 1.9)) * 0.74
  }

  private fade(c: RGB, z: number, dist: number, p: Palette): RGB {
    return mix(c, p.canvasEdge, this.haze(z, dist))
  }

  /* ── background ──────────────────────────────────────────────────────── */

  private background(
    p: Palette,
    pr: Projector,
    radius: number,
    tiers: Set<number>,
    rings: boolean,
  ) {
    const { ctx, w, h } = this
    const core = `rgb(${p.canvasCore[0]},${p.canvasCore[1]},${p.canvasCore[2]})`
    const edge = `rgb(${p.canvasEdge[0]},${p.canvasEdge[1]},${p.canvasEdge[2]})`
    ctx.fillStyle = edge
    ctx.fillRect(0, 0, w, h)

    // The light still falls off from the seed idea, and still literally: the
    // gradient is centred on the seed's projected position, so orbiting moves the
    // light exactly as panning does in the flat view.
    const o = this.origin
    if (o?.on) {
      const r = Math.max(w, h) * 0.95
      const g = ctx.createRadialGradient(o.x, o.y, 0, o.x, o.y, r)
      g.addColorStop(0, core)
      g.addColorStop(1, edge)
      ctx.fillStyle = g
      ctx.fillRect(0, 0, w, h)
    }

    if (rings) this.groundRings(p, pr, radius)
    this.axis(p, pr, radius, tiers)
  }

  /**
   * The depth rings, on the ground plane, in perspective.
   *
   * The same `ringRadius` the layout uses, so a ring here is the same ring as over
   * there — one reasoning depth. Drawn as a projected polygon rather than an
   * ellipse because a tilted circle is an ellipse only under an orthographic
   * camera; under perspective it is a conic, and `ctx.ellipse` would be subtly
   * wrong in exactly the way that makes a floor look like a decal.
   */
  private groundRings(p: Palette, pr: Projector, radius: number) {
    const { ctx } = this
    ctx.save()
    ctx.strokeStyle = rgba(p.grid, p.ringAlpha * 1.5)
    ctx.lineWidth = 1
    const STEPS = 72
    for (let d = 1; d <= 8; d++) {
      const r = ringRadius(d)
      if (r > radius * 1.5) break
      let started = false
      ctx.beginPath()
      for (let i = 0; i <= STEPS; i++) {
        const a = (i / STEPS) * TAU
        const q = pr.project(Math.cos(a) * r, 0, Math.sin(a) * r)
        if (!q.on) {
          started = false
          continue
        }
        if (started) ctx.lineTo(q.x, q.y)
        else {
          ctx.moveTo(q.x, q.y)
          started = true
        }
      }
      ctx.stroke()
    }
    ctx.restore()
  }

  /**
   * The evidence axis: a post at the origin with a hoop at every tier in use.
   *
   * A hoop appears because something is at that height — so the axis draws itself
   * from the data rather than being a fixed ruler with empty gradations. On a graph
   * nobody has checked there is no post and no hoop, and the whole graph lies flat
   * on the floor. That reads as the truth about the graph rather than as a missing
   * feature, which is the entire reason the axis is bound to occupancy.
   */
  private axis(p: Palette, pr: Projector, radius: number, tiers: Set<number>) {
    const { ctx } = this
    const top = Math.max(...tiers, 0)
    if (top <= 0) return
    ctx.save()
    const STEPS = 72
    for (const tier of tiers) {
      if (tier <= 0) continue
      const y = tier * TIER_HEIGHT
      ctx.strokeStyle = rgba(p.grid, p.ringAlpha * 1.9)
      ctx.lineWidth = 1
      let started = false
      ctx.beginPath()
      for (let i = 0; i <= STEPS; i++) {
        const a = (i / STEPS) * TAU
        const q = pr.project(Math.cos(a) * radius, y, Math.sin(a) * radius)
        if (!q.on) {
          started = false
          continue
        }
        if (started) ctx.lineTo(q.x, q.y)
        else {
          ctx.moveTo(q.x, q.y)
          started = true
        }
      }
      ctx.stroke()
    }
    // The post, from the plane to the highest tier anything reached. Fainter than
    // the hoops: the hoops carry the scale, and this only has to say where the
    // axis is. Any stronger and a line up the middle of the graph reads as a
    // strand rather than as a gradation.
    const a = pr.project(0, 0, 0)
    const b = pr.project(0, top * TIER_HEIGHT, 0)
    if (a.on && b.on) {
      ctx.strokeStyle = rgba(p.grid, p.ringAlpha * 1.5)
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
    }
    ctx.restore()
  }

  /* ── strands ─────────────────────────────────────────────────────────── */

  private strandColour(n: ReasonNode, p: Palette, ch = channelOf(n)): RGB {
    const base = p.channel[ch]
    // A rewritten claim's tension rated the words it replaced (`drawnRatingsOf`).
    const { tension } = drawnRatingsOf(n)
    if (ch !== 'tension' && ch !== 'dead' && ch !== 'refuted' && tension > 0.28) {
      return mix(base, p.channel.tension, clamp((tension - 0.28) / 0.72) * 0.55)
    }
    return base
  }

  /**
   * Project a 3D polyline, returning screen points and their own scales.
   *
   * Stops at the first vertex behind the near plane rather than skipping it: a
   * strand that straddles the camera would otherwise be drawn as a line joining
   * the two halves across the whole stage.
   */
  private projectPath(pr: Projector, path: Vec3[]): { pts: Pt[]; scales: number[]; z: number } {
    const pts: Pt[] = []
    const scales: number[] = []
    let zsum = 0
    for (const v of path) {
      const q = pr.project(v.x, v.y, v.z)
      if (!q.on) break
      pts.push({ x: q.x, y: q.y })
      scales.push(q.s)
      zsum += q.z
    }
    return { pts, scales, z: pts.length ? zsum / pts.length : Infinity }
  }

  private drawStrand(it: Extract<Item, { kind: 'strand' }>, p: Palette, dist: number, dim: number) {
    const { ctx } = this
    const n = it.n
    const now = Date.now()
    const age = now - this.born(n)
    const { extend, consolidate, nodeAt } = this.phases(age)
    if (extend <= 0) return

    const dead = n.terminal && !n.outcome
    const wither = !dead
      ? 0
      : this.calm
        ? 1
        : clamp((age - nodeAt - 300 / this.tempo) / (900 / this.tempo))
    const ch = channelOf(n)
    const col = this.fade(this.strandColour(n, p, ch), it.z, dist, p)

    // Draw-on: the strand extends toward its child rather than appearing whole.
    // The four-phase filopodia choreography of the flat view is deliberately not
    // reproduced — this view is for reading a graph you already have, and a
    // probing growth cone in perspective is motion without information.
    const count = Math.max(2, Math.round(it.pts.length * easeOutCubic(extend)))
    const start = it.stale ? Math.ceil((it.pts.length - 1) * 0.14) : 0
    if (count <= start + 1) return
    const pts = it.pts.slice(start, count)
    const scales = it.scales.slice(start, count)
    if (pts.length < 2) return

    // CONFIDENCE IS OPACITY HERE. In the flat view it is width; under perspective
    // width is distance, and this is the channel that survives the projection.
    // The middle of it for a claim the reader rewrote, as the flat view draws.
    const conf = 0.24 + drawnRatingsOf(n).confidence * 0.68
    const maturity = 0.45 + 0.55 * (this.calm ? 1 : consolidate)
    const alpha = conf * p.baseAlpha * dim * maturity * (1 - wither * 0.62)

    // Taper: thick at the premise, thin at the claim — the one geometric binding
    // that survives, because it is a RATIO along the strand rather than a size.
    const halfAt = (t: number) => {
      const i = Math.min(scales.length - 1, Math.round(t * (scales.length - 1)))
      const s = scales[i] ?? 0.5
      const taper = 1 - t * 0.55
      return clamp(STRAND_W * 0.5 * s * taper, 0.25, 22)
    }

    ctx.save()
    ctx.fillStyle = rgba(col, alpha)
    // A strand that has finished drawing on is the same polygon on every frame
    // the camera holds still, and the camera holds still on most frames. Keep
    // it as a path, keyed on the camera object, the geometry it was lifted from
    // and the detachment that trims its start; everything else about it —
    // colour, alpha, wither — is paint, not shape.
    if (extend >= 1 && this.cam) {
      const hit = this.ribbons.get(n.id)
      if (hit && hit.cam === this.cam && hit.rev === it.rev && hit.stale === it.stale) {
        ctx.fill(hit.path)
      } else {
        const path = new Path2D()
        addPoly(path, ribbon(pts, halfAt))
        this.ribbons.set(n.id, { cam: this.cam, rev: it.rev, stale: it.stale, path })
        ctx.fill(path)
      }
    } else {
      tracePoly(ctx, ribbon(pts, halfAt))
      ctx.fill()
    }
    ctx.restore()
  }

  /** Phase progressions, matching the flat view's clock so a node lands at the
   *  same moment in both and the caption layer needs no second timing. */
  private phases(age: number) {
    const t = this.tempo
    const probeMs = this.calm ? 1 : BASE.probe / t
    const extendMs = this.calm ? 170 : BASE.extend / t
    const consMs = this.calm ? 170 : BASE.consolidate / t
    return {
      extend: clamp((age - probeMs) / extendMs),
      consolidate: clamp((age - probeMs - extendMs) / consMs),
      nodeAt: probeMs + extendMs,
      grown: probeMs + extendMs + consMs,
    }
  }

  /**
   * A bridge, arcing over the graph.
   *
   * The reason this view exists. In 2D this is a chord drawn straight across the
   * canvas, passing over every branch between its two ends and reading as though
   * it touched them; here it leaves the plane, climbs in proportion to the
   * distance it spans, and comes down on the far branch. Nothing in between is
   * implicated.
   *
   * Drawn as a stroked line rather than a tapered ribbon, and that is deliberate:
   * a bridge is not a derivation. It has no premise end and no claim end, so
   * giving it a taper would state a direction the data does not have.
   */
  private drawBridge(it: Extract<Item, { kind: 'bridge' }>, p: Palette, dist: number, dim: number) {
    const { ctx } = this
    const now = Date.now()
    const age = now - this.born(it.n)
    // Drawn on whole under reduced motion, like every other growth here.
    const t = this.calm && age >= 0 ? 1 : clamp(age / (1400 / this.tempo))
    if (t <= 0) return
    const count = Math.max(2, Math.round(it.pts.length * easeOutCubic(t)))
    const start = it.stale ? Math.ceil((it.pts.length - 1) * 0.14) : 0
    if (count <= start + 1) return
    const pts = it.pts.slice(start, count)
    if (pts.length < 2) return

    const col = this.fade(p.channel.insight, it.z, dist, p)
    ctx.save()
    ctx.strokeStyle = rgba(col, (it.foreign ? 0.5 : 0.72) * dim * p.baseAlpha)
    // Width from the apex's own scale, so it recedes with everything else.
    const mid = it.scales[Math.floor(it.scales.length / 2)] ?? 0.5
    ctx.lineWidth = clamp(3.4 * mid, 0.7, 9)
    ctx.lineJoin = 'round'
    // A bridge that leaves this exploration is dashed, for the same reason the
    // flat view dashes its stub: the far end is a marker for where another graph
    // would be, not that graph.
    if (it.foreign) ctx.setLineDash([7, 6])
    ctx.beginPath()
    tracePoly(ctx, pts)
    ctx.stroke()
    ctx.setLineDash([])
    ctx.restore()
  }

  /**
   * A node's drop line to the plane.
   *
   * Faint, and never in the node's own hue: a stem in the channel colour reads as
   * another strand — as though the claim were derived from the floor. It is a
   * measurement, so it is drawn in the same ink as the rings it measures against.
   */
  private drawStem(it: Extract<Item, { kind: 'stem' }>, p: Palette, dim: number) {
    const { ctx } = this
    ctx.save()
    ctx.strokeStyle = rgba(p.grid, (p.theme === 'dark' ? 0.5 : 0.38) * dim)
    ctx.lineWidth = 1
    ctx.setLineDash([2, 3])
    ctx.beginPath()
    ctx.moveTo(it.a.x, it.a.y)
    ctx.lineTo(it.b.x, it.b.y)
    ctx.stroke()
    ctx.setLineDash([])
    // A tick where it meets the plane, so the foot is locatable even when the stem
    // is nearly end-on to the camera.
    const r = clamp(3 * it.b.s, 0.8, 4)
    ctx.fillStyle = rgba(p.grid, 0.6 * dim)
    ctx.beginPath()
    ctx.arc(it.b.x, it.b.y, r, 0, TAU)
    ctx.fill()
    ctx.restore()
  }

  /* ── nodes ───────────────────────────────────────────────────────────── */

  private drawNode(
    it: Extract<Item, { kind: 'node' }>,
    p: Palette,
    dist: number,
    dim: number,
    selected: boolean,
    hovered: boolean,
    folded: number,
  ) {
    const { ctx } = this
    const n = it.n
    const now = Date.now()
    const age = now - this.born(n)
    const { nodeAt } = this.phases(age)
    const grow = clamp((age - nodeAt) / (this.calm ? 160 : 560 / this.tempo))
    if (grow <= 0.001) return
    const e = this.calm ? grow : easeOutBack(grow, 2.2)

    const { x, y, s } = it.at
    // SCREEN SIZE IS DISTANCE. One world radius for every node, so a bigger mark
    // means nearer and nothing else. Novelty and confidence are below, in the
    // channels that survive the projection.
    const r = clamp(NODE_R * s * e, R_MIN, R_MAX)

    const dead = n.terminal && !n.outcome
    const wither = !dead
      ? 0
      : this.calm
        ? 1
        : clamp((age - nodeAt - 300 / this.tempo) / (900 / this.tempo))
    const liveCh = liveChannelOf(n)
    const raw = this.strandColour(n, p, liveCh)
    const refuted = liveCh === 'refuted'
    const base = dead && !refuted ? mix(raw, p.channel.dead, wither) : raw
    const col = this.fade(base, it.z, dist, p)

    const kindCh = kindChannelOf(n)
    const insight = kindCh === 'insight' && !dead
    // The flat view's predicate, not a second copy of it: the two cameras must
    // agree about which nodes have no centre yet.
    const hollow = hollowGlyph(n)
    const tension = kindCh === 'tension'

    // Nothing under reduced motion (`pulseLevel`), as in the flat view.
    const pulse = pulseLevel(now - pulses.of(n), this.calm)
    const pulsing = pulse > 0

    // Outward, in screen space, from the projected seed. Every radial mark below
    // hangs off this — and it has to be computed after projection, because a spoke
    // pointing away from the seed in the world points anywhere on screen.
    const o = this.origin
    let ux = 1
    let uy = 0
    if (o?.on) {
      const dx = x - o.x
      const dy = y - o.y
      const len = Math.hypot(dx, dy)
      if (len > 0.001) {
        ux = dx / len
        uy = dy / len
      }
    }

    ctx.save()

    // Halo — novelty, and here it is the STRENGTH that carries the number while
    // the extent goes with distance like everything else. Perceptually that is the
    // right half to keep: a glow is read as how hard it is glowing, not measured.
    // None for a claim with no rating to glow by (`drawnGlowOf`).
    const glow = drawnGlowOf(n)
    if (!dead && !refuted && (glow > 0.25 || pulsing || selected || hovered)) {
      const spread = r * (2.4 + glow * 3.4 + pulse * 3 + (selected ? 1.6 : hovered ? 0.9 : 0))
      ctx.globalCompositeOperation = p.glowMode
      const g = ctx.createRadialGradient(x, y, 0, x, y, spread)
      const hue = washed(col, p)
      const a =
        (p.theme === 'dark' ? 0.34 : 0.13) *
        (0.35 + glow * 0.65) *
        (1 + pulse) *
        dim *
        (1 - this.haze(it.z, dist) * 0.6)
      // The same square-law falloff as the flat view's halo, for the same
      // reason: a ramp straight to the rim reads as fog wherever haloes meet.
      // Extent needs no zoom squeeze here — `r` carries the perspective scale,
      // so distance already collapses a far bloom toward a point of light.
      g.addColorStop(0, rgba(hue, a))
      g.addColorStop(0.25, rgba(hue, a * 0.42))
      g.addColorStop(0.55, rgba(hue, a * 0.12))
      g.addColorStop(1, rgba(hue, 0))
      ctx.fillStyle = g
      ctx.beginPath()
      ctx.arc(x, y, spread, 0, TAU)
      ctx.fill()
      ctx.globalCompositeOperation = 'source-over'
    }

    const alpha = (0.94 + (0.42 - 0.94) * wither) * dim * p.baseAlpha

    // The flat view's notch, cut from the screen spoke so it holds still while
    // you orbit (`canvas/notch.ts`).
    const outline = (rr: number) => {
      if (this.stood?.has(n.id)) traceNotched(ctx, x, y, rr, Math.atan2(uy, ux))
      else {
        ctx.beginPath()
        ctx.arc(x, y, rr, 0, TAU)
      }
    }

    if (hollow) {
      ctx.strokeStyle = rgba(col, alpha)
      ctx.lineWidth = Math.max(0.8, r * 0.42)
      outline(r * 1.12)
      ctx.stroke()
    } else {
      ctx.fillStyle = rgba(
        hot(col, (0.22 + pulse * 0.4) * (1 - wither), p),
        alpha * (1 - wither * 0.55),
      )
      outline(r)
      ctx.fill()
    }

    // The retraction bulb: an ending that was reached, not one that trailed off.
    if (wither > 0) {
      const ink = p.theme === 'dark' ? col : mix(col, p.deepen, 0.4)
      const bulbR = Math.max(2, r * 1.4)
      ctx.strokeStyle = rgba(ink, 0.5 * wither * dim * p.baseAlpha)
      ctx.lineWidth = Math.max(0.8, r * 0.18)
      ctx.beginPath()
      ctx.arc(x, y, bulbR, 0, TAU)
      ctx.stroke()
      const b0 = bulbR + Math.max(1.4, r * 0.5)
      const arm = bulbR * 0.8
      ctx.beginPath()
      ctx.moveTo(x + ux * b0 - uy * arm, y + uy * b0 + ux * arm)
      ctx.lineTo(x + ux * b0 + uy * arm, y + uy * b0 - ux * arm)
      ctx.stroke()
    }

    if (tension) {
      const a = r * (p.theme === 'dark' ? 0.52 : 0.84)
      const strike = p.theme === 'dark' ? p.deepen : mix(col, p.deepen, 0.74)
      ctx.strokeStyle = rgba(strike, (p.theme === 'dark' ? 0.9 : 0.82) * dim * (1 - wither * 0.45))
      ctx.lineWidth = Math.max(0.8, r * (p.theme === 'dark' ? 0.24 : 0.22))
      ctx.lineCap = p.theme === 'dark' ? 'butt' : 'round'
      ctx.beginPath()
      ctx.moveTo(x - a, y - a)
      ctx.lineTo(x + a, y + a)
      ctx.moveTo(x + a, y - a)
      ctx.lineTo(x - a, y + a)
      ctx.stroke()
      ctx.lineCap = 'butt'
    }

    if (refuted) {
      const a = r * 0.86
      const gapR = Math.max(0.6, r * 0.34)
      ctx.strokeStyle = rgba(hot(col, 0.62, p), 0.95 * dim * p.baseAlpha)
      ctx.lineWidth = Math.max(0.8, r * 0.2)
      ctx.lineCap = 'round'
      // Across the screen spoke, so the pair is stable while you orbit.
      const px = -uy
      const py = ux
      for (const sgn of [-1, 1]) {
        const ox = ux * gapR * sgn * 0.5
        const oy = uy * gapR * sgn * 0.5
        ctx.beginPath()
        ctx.moveTo(x + ox - px * a, y + oy - py * a)
        ctx.lineTo(x + ox + px * a, y + oy + py * a)
        ctx.stroke()
      }
      ctx.lineCap = 'butt'
    }

    // The flat view's broken rim where the check cites a retracted work, open on
    // the screen spoke so it holds still while you orbit (`canvas/rim.ts`).
    const withdrawn = citesRetracted(n)
    if ((withdrawn || checkedAgainstSources(n)) && wither < 0.6) {
      ctx.strokeStyle = rgba(hot(col, p.theme === 'dark' ? 0.55 : 0.5, p), 0.7 * dim * p.baseAlpha)
      if (withdrawn) {
        ctx.lineWidth = Math.max(0.7, r * 0.16)
        ctx.lineCap = 'round'
        traceBrokenRim(ctx, x, y, r, hollow, Math.atan2(uy, ux))
        ctx.stroke()
        ctx.lineCap = 'butt'
      } else {
        ctx.lineWidth = Math.max(0.5, r * 0.14)
        outline(hollow ? r * 1.42 : r)
        ctx.stroke()
      }
    }

    if (insight) {
      ctx.strokeStyle = rgba(col, 0.75 * dim)
      ctx.lineWidth = Math.max(0.7, r * 0.2)
      ctx.beginPath()
      ctx.arc(x, y, r * 2.15, 0, TAU)
      ctx.stroke()
    }

    if (n.editedAt) {
      ctx.strokeStyle = rgba(p.accent, 0.9 * dim)
      ctx.lineWidth = Math.max(0.7, r * 0.22)
      ctx.setLineDash([2, 2.6])
      ctx.beginPath()
      ctx.arc(x, y, r * 2.8, 0, TAU)
      ctx.stroke()
      ctx.setLineDash([])
    }

    // A folded branch: the same two outward chevrons, on the screen spoke.
    if (n.collapsed && folded > 0) {
      const baseD = Math.max(r * 3.95, r + 15)
      const arm = Math.max(2.4, r * 1.15)
      const step = Math.max(2.4, r * 1.1)
      const half = 0.62
      ctx.lineWidth = Math.max(0.9, r * 0.26)
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      for (const i of [0, 1]) {
        const d = baseD + i * step
        const ax = x + ux * d
        const ay = y + uy * d
        ctx.strokeStyle = rgba(col, 0.8 * (i ? 0.62 : 1) * dim * p.baseAlpha)
        ctx.beginPath()
        for (const sgn of [-1, 1]) {
          const c = Math.cos(half * sgn)
          const sn = Math.sin(half * sgn)
          ctx.moveTo(ax, ay)
          ctx.lineTo(ax + (-ux * c + uy * sn) * arm, ay + (-ux * sn - uy * c) * arm)
        }
        ctx.stroke()
      }
      ctx.lineCap = 'butt'
      ctx.lineJoin = 'miter'
    }

    if (selected || hovered) {
      ctx.strokeStyle = rgba(p.text1, selected ? 0.7 : hoverRingAlpha(p))
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.arc(x, y, r + (selected ? 6 : 4.5), 0, TAU)
      ctx.stroke()
    }

    ctx.restore()
  }

  /**
   * The seed, as a body rather than a disc.
   *
   * The one mark in this view that gets a light direction, and it is not a
   * lighting model creeping in: the flat view already draws the orb as a graded
   * body with a rim, and in perspective a flat disc at the centre of a volume
   * reads as a hole in it. The gradient is offset toward the camera's own up
   * vector, so it stays consistent as you orbit instead of implying a lamp
   * somewhere in the room.
   */
  private drawRoot(it: Extract<Item, { kind: 'root' }>, p: Palette, busy: boolean, sel: boolean) {
    const { ctx } = this
    const { x, y, s } = it.at
    const r = clamp(ROOT_R * s, 6, 520)
    // The same accumulated breath as the flat view: a phase, not a wall-clock
    // modulus, so it cannot pop when `busy` flips. Advanced by the frame's
    // elapsed time in units of a 60 Hz frame, not by one step per frame: the loop
    // draws at a third of the rate at rest (`cadence.ts`), and a per-frame step
    // would have slowed the breath to a third along with it.
    this.breath += 0.0016 * (this.dt / (1000 / 60)) * (busy ? 2.1 : 1)
    const pulse = 0.5 + 0.5 * Math.sin(this.breath)

    ctx.save()
    ctx.globalCompositeOperation = p.glowMode
    const halo = ctx.createRadialGradient(x, y, r * 0.4, x, y, r * (1.9 + pulse * 0.3))
    const hue = washed(p.channel.signal, p)
    halo.addColorStop(0, rgba(hue, p.theme === 'dark' ? 0.3 : 0.12))
    halo.addColorStop(1, rgba(hue, 0))
    ctx.fillStyle = halo
    ctx.beginPath()
    ctx.arc(x, y, r * (1.9 + pulse * 0.3), 0, TAU)
    ctx.fill()
    ctx.globalCompositeOperation = 'source-over'

    const g = ctx.createRadialGradient(x - r * 0.3, y - r * 0.34, r * 0.05, x, y, r)
    g.addColorStop(0, rgba(p.hi, p.theme === 'dark' ? 0.95 : 0.98))
    g.addColorStop(0.55, rgba(mix(p.hi, p.channel.signal, 0.5), p.theme === 'dark' ? 0.5 : 0.5))
    g.addColorStop(1, rgba(p.channel.signal, p.theme === 'dark' ? 0.16 : 0.2))
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(x, y, r, 0, TAU)
    ctx.fill()

    ctx.strokeStyle = rgba(
      mix(p.channel.signal, p.hi, 0.4),
      (p.theme === 'dark' ? 0.28 : 0.3) + pulse * 0.2,
    )
    ctx.lineWidth = Math.max(1, r * 0.012)
    ctx.beginPath()
    ctx.arc(x, y, r * 0.985, 0, TAU)
    ctx.stroke()

    if (sel) {
      ctx.strokeStyle = rgba(p.text1, 0.55)
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.arc(x, y, r * 1.06, 0, TAU)
      ctx.stroke()
    }
    ctx.restore()
  }

  private breath = 0
  /** This frame's elapsed time, for the breath. */
  private dt = 16
  /** This frame's claims an objection below stood against (`stoodAgainst`). */
  private stood: ReadonlySet<string> | null = null

  /**
   * Whether anything the last frame drew was mid-transition — a strand growing,
   * a node arriving, a pulse. The flat renderer's flag, for the same loop and the
   * same reason: see `Renderer.settling`.
   */
  settling = false

  /* ── frame ───────────────────────────────────────────────────────────── */

  draw(f: SpatialFrame, cam: Cam, dt = 16, busy = false) {
    const { ctx } = this
    this.tempo = f.tempo
    this.cam = cam
    this.dt = dt
    motion.step(f.session.nodes, f.session.order, dt)

    // The picture's centre eases toward the visible field's (see `aim`), and
    // every projected ribbon is stale the moment it moves — they are screen
    // space, keyed on the camera, and the camera did not change.
    const shifted = this.easeCentre(dt)
    if (shifted) this.ribbons.clear()

    // Did anything the overlays draw move this frame? The same question the
    // flat view answers on `screen.frame`, with the camera object standing in
    // for the viewport — see `screen.ts` and finding 6.
    {
      const moved =
        motion.moving ||
        shifted ||
        cam !== this.lastCam ||
        f.rev !== this.lastRev ||
        f.playhead !== this.lastPlayhead ||
        f.playheadAt !== this.lastPlayheadAt ||
        this.w !== this.lastW ||
        this.h !== this.lastH
      this.lastCam = cam
      this.lastRev = f.rev
      this.lastPlayhead = f.playhead
      this.lastPlayheadAt = f.playheadAt
      this.lastW = this.w
      this.lastH = this.h
      if (moved) screen.frame++
    }
    // The geometry key: the motion epoch while the layout eases, the revision
    // otherwise — the flat view's `edgeFor` scheme, negative so the two
    // numberings never collide.
    const geomKey = motion.moving ? -motion.epoch - 1 : f.rev
    const now = Date.now()
    // One and a half growths, as the flat view keeps — of the growth `phases`
    // draws, so reduced motion's third of a second rather than five (D191).
    const youngFor = this.phases(0).grown * 1.5
    let young = false
    let pulsing = false

    // The same replay clock as the flat view, so scrubbing the timeline replays
    // growth here too instead of flicking nodes into being.
    this.birthAt.clear()
    if (f.playhead !== null) {
      const step = BASE.beat / f.tempo
      const head = f.playhead - 1
      for (let i = 0; i <= head && i < f.session.order.length; i++) {
        this.birthAt.set(f.session.order[i]!, f.playheadAt - (head - i) * step)
      }
    }
    growth.bornAt = (id, birth) => this.birthAt.get(id) ?? birth
    growth.landsAfter = this.phases(0).nodeAt
    growth.calm = this.calm

    const pr = this.projector(cam, this.middle)
    const { session, visible, focus, guideKeep, palette: p } = f
    const dist = cam.dist
    const tainted = taintMap(session)
    const evidenceStale = evidenceStaleMap(session)
    this.stood = stoodAgainst(session)

    ctx.save()
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)
    ctx.clearRect(0, 0, this.w, this.h)

    // Everything projects once, into one flat list, and the list is what gets
    // sorted. Projecting inside each draw call would mean projecting a strand's
    // parent twice and would make the sort impossible.
    this.placed.clear()
    this.items = []
    let radius = 420
    const tiers = new Set<number>()

    let root: ReasonNode | null = null
    for (const id of session.order) {
      const n = session.nodes[id]
      if (!n || !visible.has(id)) continue
      const wv = this.worldOf(n)
      const q = pr.project(wv.x, wv.y, wv.z)
      this.placed.set(id, q)
      radius = Math.max(radius, Math.hypot(wv.x, wv.z))
      tiers.add(contactTier(n))
      if (!young && now - this.born(n) < youngFor) young = true
      if (!pulsing && pulseLevel(now - pulses.of(n), this.calm) > 0) pulsing = true
      if (n.parentId === null) {
        root = n
        this.origin = q
      }
      if (!q.on) continue
      if (n.parentId === null) this.items.push({ kind: 'root', z: q.z, n, at: q })
      else this.items.push({ kind: 'node', z: q.z, n, at: q })
    }
    // The background goes down whether or not there is a graph to put on it. An
    // empty session — cleared, or filtered to nothing — should read as an empty
    // room rather than as a black rectangle, which is what a failed render looks
    // like. The item passes below are the part that needs a root.
    this.background(p, pr, radius, tiers, f.rings)
    screen.animating = young || f.playhead !== null
    this.settling = young || pulsing || this.centring
    if (!root) {
      ctx.restore()
      this.publish()
      return
    }

    for (const id of session.order) {
      const n = session.nodes[id]
      if (!n || !visible.has(id) || n.parentId === null) continue
      if (!visible.has(n.parentId)) continue
      const parent = session.nodes[n.parentId]
      if (!parent) continue

      let g = this.paths.get(id)
      if (!g || g.rev !== geomKey) {
        const a = motion.at(parent)
        const b = motion.at(n)
        // The SAME curve the flat view draws, from the same seed, on the ground
        // plane — so the two views show one graph rather than two drawings of one.
        const plane = strandPath(a, b, hash01(n.id), 22)
        g = { rev: geomKey, path: liftPath(plane, contactHeight(parent), contactHeight(n)) }
        this.paths.set(id, g)
      }
      const { pts, scales, z } = this.projectPath(pr, g.path)
      const stale =
        (parent.outcome !== undefined && writtenAt(n) < parent.outcome.recordedAt) ||
        tainted.has(n.id) ||
        evidenceStale.has(n.id)
      if (pts.length >= 2) {
        this.items.push({ kind: 'strand', z, n, pts, scales, stale, rev: geomKey })
      }

      // The stem: only where there is a height to read.
      if (contactTier(n) > 0) {
        const up = this.placed.get(id)
        const at = motion.at(n)
        const foot = pr.project(at.x, 0, at.y)
        if (up?.on && foot.on) this.items.push({ kind: 'stem', z: up.z, n, a: up, b: foot })
      }

      // A bridge to another branch of this graph.
      if (n.bridgeFrom) {
        const otherId = n.bridgeFrom.find((x) => x !== n.parentId)
        const other = otherId ? session.nodes[otherId] : undefined
        if (other && visible.has(other.id)) {
          let a = this.arcs.get(id)
          if (!a || a.rev !== geomKey) {
            a = { rev: geomKey, path: bridgeArc(this.worldOf(n), this.worldOf(other)) }
            this.arcs.set(id, a)
          }
          const got = this.projectPath(pr, a.path)
          if (got.pts.length >= 2) {
            this.items.push({
              kind: 'bridge',
              z: got.z,
              n,
              pts: got.pts,
              scales: got.scales,
              foreign: false,
              stale,
            })
          }
        }
      }

      // A bridge that leaves this exploration. In 2D this is a stub pointing at
      // nothing; here it has somewhere to point.
      if (n.foreignFrom) {
        const station = foreignStation(hash01(n.foreignFrom.sessionId), radius)
        const arc = bridgeArc(this.worldOf(n), station, 24)
        const got = this.projectPath(pr, arc)
        if (got.pts.length >= 2) {
          this.items.push({
            kind: 'bridge',
            z: got.z,
            n,
            pts: got.pts,
            scales: got.scales,
            foreign: true,
            stale,
          })
        }
      }
    }

    // Painter's algorithm: furthest first. At this scale a full sort per frame is
    // a few hundred comparisons — cheaper than any depth buffer this renderer
    // could fake, and it gets transparency right, which a depth buffer would not.
    this.items.sort((x, y) => y.z - x.z)

    // The flat view's rule: a claim a rethink replaced recedes, unless selected.
    const prior = priorVersionIds(session)
    const dimFor = (id: string) => {
      const ink = prior.has(id) && id !== f.selectedId ? PRIOR_VERSION_INK : 1
      if (focus && !focus.has(id)) return (p.theme === 'dark' ? 0.17 : 0.22) * ink
      if (guideKeep && !guideKeep.has(id)) return (p.theme === 'dark' ? 0.12 : 0.18) * ink
      return ink
    }

    for (const it of this.items) {
      const dim = dimFor(it.n.id)
      switch (it.kind) {
        case 'strand':
          this.drawStrand(it, p, dist, dim)
          break
        case 'stem':
          this.drawStem(it, p, dim)
          break
        case 'bridge':
          this.drawBridge(it, p, dist, dim)
          break
        case 'root':
          this.drawRoot(it, p, busy, it.n.id === f.selectedId)
          break
        case 'node':
          this.drawNode(
            it,
            p,
            dist,
            dim,
            it.n.id === f.selectedId,
            it.n.id === f.hoverId,
            f.folded.get(it.n.id) ?? 0,
          )
          break
      }
    }

    ctx.restore()
    this.publish()
  }

  /** Hand the label layer this frame's camera. See screen.ts. */
  private publish() {
    const o = this.origin
    screen.mode = '3d'
    screen.originX = o?.x ?? this.middle.x
    screen.originY = o?.y ?? this.middle.y
    screen.orbR = o?.on ? clamp(ROOT_R * o.s, 6, 520) : 0
    // Normalised so the overlay's one zoom threshold works in both views: the
    // perspective scale at the seed is the same quantity as the flat view's `k`
    // — pixels per world unit — which is why no conversion is needed beyond a
    // clamp against a camera flown right up to a node.
    screen.zoom = clamp(o?.on ? o.s : 0.5, 0.05, 2)
    screen.place = (id: string) => {
      const q = this.placed.get(id)
      if (!q || !q.on) return { x: 0, y: 0, ux: 1, uy: 0, s: 1, z: q?.z ?? 0, on: false }
      let ux = 1
      let uy = 0
      if (o?.on) {
        const dx = q.x - o.x
        const dy = q.y - o.y
        const len = Math.hypot(dx, dy)
        if (len > 0.001) {
          ux = dx / len
          uy = dy / len
        }
      }
      return { x: q.x, y: q.y, ux, uy, s: q.s, z: q.z, on: true }
    }
  }

  /**
   * Nearest node to a screen point.
   *
   * Nearest in SCREEN space, then nearest to the eye among those — which is what
   * makes clicking a cluster pick the one in front rather than the one whose
   * centre happens to be a pixel closer. Reach scales with the mark's own
   * projected size, so a node you have flown up to is easy to hit and a distant
   * one does not steal clicks from its neighbours. `floor` is the smallest reach a
   * mark gets — wider for a finger's tap, which `SpatialCanvas` tries only after
   * the ordinary reach found nothing (`tapReach`).
   */
  hitTest(f: SpatialFrame, sx: number, sy: number, floor = 11): string | null {
    let best: string | null = null
    let bestZ = Infinity
    for (const id of f.session.order) {
      if (!f.visible.has(id)) continue
      const q = this.placed.get(id)
      if (!q?.on) continue
      const n = f.session.nodes[id]
      if (!n) continue
      const r =
        n.parentId === null
          ? clamp(ROOT_R * q.s, 6, 520)
          : Math.max(floor, clamp(NODE_R * q.s, R_MIN, R_MAX) * 2.2) * (n.collapsed ? 1.35 : 1)
      if (Math.hypot(sx - q.x, sy - q.y) > r) continue
      if (q.z < bestZ) {
        bestZ = q.z
        best = id
      }
    }
    return best
  }
}
